import { create } from 'zustand';
import { AgentKpiEvaluationResult } from '../models/CXModel';

/**
 * An agent's live dialer status, as returned by the backend.
 *
 * - `available` — logged into the dialer and ready for a contact
 * - `idle` — logged in but NOT ready for a contact (not ready / do-not-send)
 * - `on_call` — on a live contact; their room locks, same as a locked office
 * - `logged_out` — signed out of the dialer, but still present in NexGen
 * - `unknown` — a provider state we don't recognise
 */
export type CxAgentStatus = 'available' | 'idle' | 'on_call' | 'logged_out' | 'unknown';

export interface CxAgentStatusView {
  status: CxAgentStatus;
  /** When the backend last confirmed this status with the dialer. ISO 8601. */
  as_of: string;
}

/**
 * Statuses keyed by VO user pid. A user with no entry has no known status —
 * that is not the same as being available, so those render without an indicator.
 */
export type CxAgentStatusMap = Record<string, CxAgentStatusView>;

// ---- Define the store state type ----
interface CxFloorState {
  agentsKpisData: AgentKpiEvaluationResult[];
  isLoadingAgentsKPIsData: boolean;
  hasLoadedAgentsKPIsData: boolean;
  agentStatuses: CxAgentStatusMap;

  setAgentsKpisData: (data: AgentKpiEvaluationResult[]) => void;
  setHasLoadedAgentsKPIsData: (value: boolean) => void;
  setIsLoadingAgentsKPIsData: (value: boolean) => void;
  setAgentStatuses: (statuses: CxAgentStatusMap) => void;
}

// ---- Store Definition ----
export const useCxFloorStore = create<CxFloorState>((set) => ({
  hasLoadedAgentsKPIsData: false,
  setHasLoadedAgentsKPIsData: (value) => set({ hasLoadedAgentsKPIsData: value }),
  isLoadingAgentsKPIsData: false,
  setIsLoadingAgentsKPIsData: (value) => set({ isLoadingAgentsKPIsData: value }),
  agentsKpisData: [],
  setAgentsKpisData: (data) => set({ agentsKpisData: data }),
  agentStatuses: {},
  setAgentStatuses: (statuses) => set({ agentStatuses: statuses }),
}));
